/**
 * PDF text extraction, column-aware.
 *
 * LinkedIn profile exports are TWO-COLUMN: a narrow left sidebar (Contact,
 * Top Skills, Languages, Certifications) beside a main column (Summary,
 * Experience, Education). Grouping text runs by y-coordinate alone interleaves
 * them — a sidebar line lands in the middle of a job entry — which scatters the
 * skills block and fills the Experience candidates with debris that then
 * false-matches the taxonomy.
 *
 * So: split by x FIRST, then order each column top-to-bottom.
 * Shared by the browser and the test harness so both see identical text.
 */

const SIDEBAR_RATIO = 0.34; // sidebar occupies roughly the left third

/**
 * @param {object} pdfjs  the pdfjs module (caller imports the right build)
 * @param {Uint8Array} data
 * @returns {Promise<{sidebar:string[], main:string[], all:string[]}>}
 */
export async function extractColumns(pdfjs, data) {
  const doc = await pdfjs.getDocument({ data }).promise;
  const sidebar = [], main = [];

  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const width = page.getViewport({ scale: 1 }).width;
    const cut = width * SIDEBAR_RATIO;
    const content = await page.getTextContent();

    // Group into lines per column: key on (column, rounded y).
    const rows = new Map();
    for (const it of content.items) {
      if (!it.str || !it.str.trim()) continue;
      const x = it.transform[4];
      const y = Math.round(it.transform[5]);
      const col = x < cut ? "s" : "m";
      const key = `${col}:${y}`;
      if (!rows.has(key)) rows.set(key, { col, y, parts: [], x });
      rows.get(key).parts.push({ x, str: it.str });
    }

    const flush = (col, target) =>
      [...rows.values()]
        .filter((r) => r.col === col)
        .sort((a, b) => b.y - a.y)
        .forEach((r) => {
          const line = r.parts.sort((a, b) => a.x - b.x).map((p) => p.str).join("").trim();
          if (line) target.push(line);
        });

    flush("s", sidebar);
    flush("m", main);
  }

  return { sidebar, main, all: [...sidebar, ...main] };
}

/** True if the document looks like a LinkedIn profile export. */
export function looksLikeLinkedIn(cols) {
  const blob = [...cols.sidebar, ...cols.main].join("\n").toLowerCase();
  return /\bexperience\b/.test(blob) && (/\btop skills\b/.test(blob) || /linkedin\.com\/in\//.test(blob));
}
